import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2,
  Camera, Compass, Sparkles, MapPin, Calendar, BookOpen, Landmark, Phone, Utensils,
  Waves, Sun, Bed, Coffee, ShoppingBag, Music, Palette, Bike, Ship, Star, Heart,
  Building2, Trees, Tent, Car, Globe } from 'lucide-react';
import { useRadioPlayer } from '../radio';

// Icon library for tile fallback (matches AdminCities picker)
const TILE_ICONS = {
  Camera, Compass, Sparkles, MapPin, Calendar, BookOpen, Landmark, Phone, Utensils,
  Waves, Sun, Bed, Coffee, ShoppingBag, Music, Palette, Bike, Ship, Star, Heart,
  Building2, Trees, Tent, Car, Globe
};
import { SITE } from '../mock/data';
import { useLang } from '../i18n';
import { publicApi, resolveImage } from '../api';
import { useFetch } from '../useFetch';
import WeatherWidget from '../components/WeatherWidget';
import SponsorBannerCarousel from '../components/SponsorBannerCarousel';
import FlashOffersSection from '../components/FlashOffersSection';
import ItinerariesSection from '../components/ItinerariesSection';
import PageMeta from '../components/PageMeta';

export default function CityPage() {
  const loc = useLocation();
  const slug = loc.pathname.split('/').filter(Boolean)[0];
  const nav = useNavigate();
  const { t, lang } = useLang();
  const { data: city, loading, error } = useFetch(`city:${slug}`, () => publicApi.city(slug), [slug]);
  // Global Template defaults — fetched once, reused as fallback for tile images below.
  // Called here (not after early returns) to keep hooks order stable across renders.
  const { data: settings } = useFetch('public-settings', () => publicApi.settings(), []);
  const { openModal: openRadioModal } = useRadioPlayer();

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-gray-400" size={40} /></div>;
  if (error || !city) {
    return (
      <div className="max-w-4xl mx-auto py-24 px-6 text-center">
        <h1 className="text-3xl font-bold text-[color:var(--yup-primary)]">404</h1>
        <p className="mt-3 text-gray-600">City not found</p>
        <Link to="/" className="inline-flex items-center gap-2 mt-6 text-[color:var(--yup-primary)] font-semibold"><ArrowLeft size={16} /> {t('backHome')}</Link>
      </div>
    );
  }

  if (city.status === 'coming_soon') {
    return (
      <div className="relative">
        <div className="absolute inset-0">
          <img src={resolveImage(city.hero)} alt={city.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative min-h-[80vh] flex flex-col items-center justify-center text-white text-center px-6">
          <h1 className="text-5xl md:text-7xl font-bold tracking-widest fade-in-up">{city.name}</h1>
          <div className="soon-badge mt-6 fade-in-up fade-in-up-delay-1 text-base px-6 py-2">{t('soon')}</div>
          <button onClick={() => nav('/')} className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-md hover:bg-white/20 transition fade-in-up fade-in-up-delay-2">
            <ArrowLeft size={16} /> {t('backHome')}
          </button>
        </div>
      </div>
    );
  }

  // ---- Global template defaults (Sistema C) — single source of truth for
  // cross-city fallback images. Already fetched above (see top of function).
  // Fallback chain (Feb 2026, consolidated):
  //   1) tile.image     ← per-city override in service_tiles (Sistema B)
  //   2) settings.city_default_{tile_id}_image  ← global Template (Sistema C)
  //   3) empty          ← render layer draws a branded placeholder with icon
  // The legacy `city.services` dict (Sistema A) is intentionally NOT read here.
  const tplFor = (tileId) => {
    if (!settings || !tileId) return '';
    // Setting keys use the bare id (e.g. `city_default_events_image`), while
    // tile ids carry a `t_` prefix (e.g. `t_events`). Try both to be safe.
    const bare = String(tileId).replace(/^t_/, '');
    return settings[`city_default_${bare}_image`] || settings[`city_default_${tileId}_image`] || '';
  };
  const resolveTileImage = (tile) => tile.image || tplFor(tile.id) || '';

  // Standard tile layout used when a city has no `service_tiles` yet.
  // Order is fixed (whatToSee → experiences → events → story → municipality → emergency).
  const STANDARD_TILES = [
    { id: 't_whatToSee',    label: t('whatToSee'),        link: '/cosa-vedere',  link_type: 'internal', icon: 'Compass'  },
    { id: 't_experiences',  label: t('experiences'),      link: '/experiences',  link_type: 'internal', icon: 'Sparkles' },
    { id: 't_events',       label: t('events'),           link: city.external_events || '', link_type: 'external', icon: 'Calendar' },
    { id: 't_story',        label: t('theStory'),         link: '/storia',       link_type: 'internal', icon: 'BookOpen' },
    { id: 't_municipality', label: t('theMunicipality'),  link: city.external_municipality || '', link_type: 'external', icon: 'Landmark' },
    { id: 't_emergency',    label: t('emergency'),        link: '/emergenze',    link_type: 'internal', icon: 'Phone'    },
    { id: 't_easypark',     label: 'EasyPark',            link: 'https://www.easypark.com/it-it', link_type: 'external', icon: 'Car' },
  ];

  const rawTiles = (Array.isArray(city.service_tiles) && city.service_tiles.length)
    ? [...city.service_tiles].filter(t => t && t.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0))
    : STANDARD_TILES.map((t, i) => ({ ...t, order: i, active: true }));

  const tiles = rawTiles.map(tile => {
    const label = typeof tile.label === 'string'
      ? tile.label
      : (tile.label?.[lang] || tile.label?.it || tile.label?.en || tile.id || '');
    const link = tile.link || '';
    const external = tile.link_type === 'external';
    const internalHref = link.startsWith('/') ? `/${slug}${link}` : `/${slug}/${link}`;
    return {
      key: tile.id || tile.key || Math.random().toString(36),
      label: (label || '').toUpperCase(),
      image: resolveTileImage(tile),
      icon: tile.icon || null,
      to: external ? null : internalHref,
      external: external ? (link || null) : null,
    };
  });
  const services = tiles;

  const cityDisplay = city.full_name?.[lang] || city.full_name?.it || city.name;
  const pageTitle = `${cityDisplay} — Cosa vedere, ristoranti e attività | YUP`;
  const pageDesc = `Guida turistica di ${cityDisplay}: cosa vedere, ristoranti, esperienze, eventi e numeri di emergenza. Tutto quello che ti serve per un viaggio indimenticabile.`;

  return (
    <div>
      <PageMeta title={pageTitle} description={pageDesc} />
      {/* HERO — municipality logo + city name overlaid on hero photo */}
      <section
        className="relative w-full overflow-hidden"
        data-testid={`city-hero-${slug}`}
      >
        <div className="relative w-full min-h-[55vh] md:min-h-[65vh] flex items-center justify-center">
          <img
            src={resolveImage(city.hero) || SITE.cityPageHeroBg}
            alt={city.name}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
          {/* Multi-layer overlay so overlaid text/logo remain readable */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/75" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.55)_75%)]" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 py-16 md:py-20 text-center text-white flex flex-col items-center">
            <h1
              className="text-3xl md:text-5xl font-bold tracking-widest fade-in-up fade-in-up-delay-1"
              style={{ textShadow: '0 4px 24px rgba(0,0,0,0.75)' }}
              data-testid={`city-hero-title-${slug}`}
            >
              {t('municipality')} {city.full_name?.[lang] || city.name}
            </h1>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`city-services-grid-${slug}`}>
          {services.map((s, idx) => {
            const Icon = s.icon ? TILE_ICONS[s.icon] : null;
            // Fallback icon so tiles without any image/icon still show a nice branded placeholder
            const PlaceholderIcon = Icon || TILE_ICONS.MapPin;
            const isRadio = s.key === 'w_radio_studiopiu' || s.key === 't_radio_studiopiu';
            const inner = (
              <div className={`service-tile w-full ${isRadio ? 'radio-tile' : ''}`} data-testid={`service-tile-${s.key}`}>
                {s.image ? (
                  <img src={resolveImage(s.image)} alt={s.label} loading="lazy" decoding="async" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[color:var(--yup-primary)]/15 via-black/50 to-black/70">
                    <PlaceholderIcon size={80} strokeWidth={1.2} className={`text-[color:var(--yup-primary)] opacity-80 ${isRadio ? 'animate-radio-beat' : ''}`}/>
                  </div>
                )}
                <div className="overlay" />
                <div className="label flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    {Icon && s.image ? <Icon size={16} className={`opacity-70 ${isRadio ? 'animate-radio-beat' : ''}`}/> : null}
                    {s.label}
                  </span>
                  {isRadio ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-red-600/90 text-white px-2 py-0.5 rounded-full animate-pulse">
                      LIVE
                    </span>
                  ) : (s.external && <ExternalLink size={18} className="opacity-90" />)}
                </div>
              </div>
            );
            const style = { animationDelay: `${0.05 + idx * 0.08}s` };
            if (isRadio) {
              return (
                <button key={s.key} type="button"
                        onClick={openRadioModal}
                        data-testid="open-radio-player"
                        className="fade-in-up text-left" style={style}>
                  {inner}
                </button>
              );
            }
            if (s.external) return <a key={s.key} href={s.external} target="_blank" rel="noopener noreferrer" className="fade-in-up" style={style}>{inner}</a>;
            return <Link key={s.key} to={s.to} className="fade-in-up" style={style}>{inner}</Link>;
          })}
        </div>

        {/* Weather widget — full-width below service tiles */}
        <div className="mt-12 fade-in-up" data-testid={`city-weather-${slug}`}>
          <WeatherWidget
            citySlug={slug}
            cityName={city.full_name?.[lang] || city.name}
            lang={lang}
            layout="wide"
            latitude={city.latitude}
            longitude={city.longitude}
            timezone={city.timezone}
          />
        </div>

        {/* Sponsor banner carousel — under the weather section */}
        <div className="mt-8 fade-in-up" data-testid={`city-banner-${slug}`}>
          <SponsorBannerCarousel citySlug={slug} />
        </div>

        {/* Editorial itineraries scoped to this city */}
        <ItinerariesSection citySlug={slug} limit={3} />

        {/* Flash offers — under the itineraries */}
        <FlashOffersSection citySlug={slug} />

        <div className="mt-12 text-center">
          <button onClick={() => nav('/')} className="inline-flex items-center gap-2 text-[color:var(--yup-primary)] font-semibold hover:underline">
            <ArrowLeft size={16} /> {t('backHome')}
          </button>
        </div>
      </section>
    </div>
  );
}
